import * as React from 'react';
import {
  logMetadata as logMetadataWithTakt,
  onVisit,
} from '../utils/logWithTakt';

/**
 * Known StormUI layout components from @amzn/storm-ui-layouts. Each layout
 * renders its root element with a `data-sui-component` attribute whose value
 * is the kebab-cased layout name. A page can only use one layout at a time.
 */
const STORM_LAYOUT_COMPONENTS = [
  'simple-layout',
  'form-layout',
  'dashboard-layout',
  'info-layout',
] as const;

type StormLayoutComponent = (typeof STORM_LAYOUT_COMPONENTS)[number];

const STORM_LAYOUT_SELECTOR = STORM_LAYOUT_COMPONENTS.map(
  (name) => `[data-sui-component="${name}"]`,
).join(',');

const isStormLayoutComponent = (
  value: string | null | undefined,
): value is StormLayoutComponent => {
  return (
    value !== null &&
    value !== undefined &&
    (STORM_LAYOUT_COMPONENTS as readonly string[]).indexOf(value) !== -1
  );
};

const findStormLayoutInDom = (): StormLayoutComponent | null => {
  const element = document.querySelector(STORM_LAYOUT_SELECTOR);
  const value = element ? element.getAttribute('data-sui-component') : null;
  return isStormLayoutComponent(value) ? value : null;
};

/**
 * How the probe resolved, logged alongside the layout as `stormLayoutSource`.
 *
 * This exists to make the reporting change auditable rather than inferred. The
 * original implementation could only ever resolve at `load`, so a visit
 * reporting `mount` or `observer` is one the load-independent probe newly
 * captured, and a visit reporting `route` is a client-side visit that was
 * previously never annotated at all. Crossed with the visit's own
 * `serverSideLoad` flag, it isolates exactly the recovered population — without
 * it, a post-deploy rise in layout visits is indistinguishable from a
 * concurrent page dial-up.
 */
type StormLayoutSource = 'mount' | 'observer' | 'load' | 'route';

type LogLayout = (
  layout: StormLayoutComponent | null,
  source: StormLayoutSource,
) => void;

/**
 * A probe arms whatever detection it needs, reports through `log` exactly
 * once, and returns a teardown that releases anything still armed.
 */
type Probe = (log: LogLayout) => () => void;

/**
 * How long after a client-side route change to keep waiting for a layout
 * before concluding the new route has none.
 *
 * The write must beat Takt's batch flush or it misses the visit entirely:
 * `addVisitMetadata` mutates the pending visit payload in place, and Takt
 * ships pending payloads every `Takt.pushInterval` (15 s) on a free-running
 * interval. A probe that concludes in `d` ms therefore loses roughly
 * `d / 15000` of visits to the flush, so this deadline is kept short — 2 s is
 * ample for a lazy route chunk and costs ~13% of the visits that hit it, which
 * are only the layout-less routes.
 */
const ROUTE_SETTLE_TIMEOUT_MS = 2000;

const noop = (): void => {};

/**
 * Watches `document.body` until a Storm layout appears, then reports it and
 * stops. Follows the one-shot pattern in `commons/utils/a3cEmbeddedChat.ts`.
 *
 * `document.body` is present for any mounted React tree; the guard covers the
 * web-component harness, where the effect can run against a detached document.
 */
const observeLayout = (
  onFound: (layout: StormLayoutComponent) => void,
): (() => void) => {
  if (typeof MutationObserver === 'undefined' || !document.body) {
    return noop;
  }
  const observer = new MutationObserver(() => {
    const layout = findStormLayoutInDom();
    if (layout) {
      onFound(layout);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
};

/**
 * Runs `callback` after the browser has had two frames to commit and paint.
 * Falls back to a macrotask where `requestAnimationFrame` is unavailable.
 */
const afterTwoFrames = (callback: () => void): (() => void) => {
  if (typeof requestAnimationFrame !== 'function') {
    const timer = window.setTimeout(callback, 0);
    return () => window.clearTimeout(timer);
  }
  let frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(callback);
  });
  return () => cancelAnimationFrame(frame);
};

/**
 * The document-load probe. Resolves in whichever of these happens first:
 *
 *   1. The layout is already in the DOM when the effect runs. Host layouts are
 *      server-rendered into the initial HTML, so this is the common case.
 *   2. A layout appears later, caught by a one-shot MutationObserver.
 *   3. The page `load` event fires with still no layout present, so the page
 *      genuinely has none and `null` is logged.
 *
 * `load` is only the deadline for concluding *absence*; it is deliberately not
 * the trigger for reporting presence. `load` waits on every subresource, which
 * on a heavy console page lands seconds after the layout is painted and
 * interactive — so a visit that ended before `load` used to log nothing at all,
 * even though the layout had rendered and the advertiser had seen it. Measured
 * over 30 days to 2026-09-17, no instrumented application exceeded 82% layout
 * coverage of its own visits, including single-route applications that should
 * have approached 100%, which is the shape of a flat pre-`load` loss rather than
 * partial adoption.
 *
 * A layout that first renders *after* `load` is still not counted. That is
 * unchanged from the original behaviour and is not currently known to occur,
 * since host layouts are server-rendered.
 */
const probeDocumentLoad: Probe = (log) => {
  const layoutAtMount = findStormLayoutInDom();
  if (layoutAtMount) {
    log(layoutAtMount, 'mount');
    return noop;
  }

  // Nothing rendered and nothing left to wait for: the page has no layout.
  if (document.readyState === 'complete') {
    log(null, 'mount');
    return noop;
  }

  const settle = (): void => log(findStormLayoutInDom(), 'load');
  const stopObserving = observeLayout((layout) => log(layout, 'observer'));
  window.addEventListener('load', settle, { once: true });

  return () => {
    stopObserving();
    window.removeEventListener('load', settle);
  };
};

/**
 * The client-side route probe, armed each time Takt records a new visit for
 * the same document.
 *
 * Takt fires the visit callback synchronously inside its `history.pushState`
 * hook, before the router has rendered the new route, so the DOM at that
 * instant still shows the *previous* route. Reading it immediately would
 * attribute the old layout to the new visit. The probe therefore waits two
 * frames for the router to commit, then:
 *
 *   1. reports the layout if one is present — for the unified campaign pages
 *      this is the parent route's layout, which persists across tab changes;
 *   2. otherwise watches for one to appear, since the new route's chunk may
 *      still be loading;
 *   3. and concludes `null` at a short deadline if none has.
 *
 * All three report `route`, so client-side annotations remain attributable
 * as a group; document-load and client-side visits are already separable by
 * the visit's own `serverSideLoad` flag.
 */
const probeRouteChange: Probe = (log) => {
  let stopObserving = noop;
  let timer = 0;

  const cancelFrames = afterTwoFrames(() => {
    const layout = findStormLayoutInDom();
    if (layout) {
      log(layout, 'route');
      return;
    }
    stopObserving = observeLayout((found) => log(found, 'route'));
    timer = window.setTimeout(
      () => log(findStormLayoutInDom(), 'route'),
      ROUTE_SETTLE_TIMEOUT_MS,
    );
  });

  return () => {
    cancelFrames();
    window.clearTimeout(timer);
    stopObserving();
  };
};

/**
 * Probes the DOM for the StormUI layout component rendered by the host
 * application (SimpleLayout, FormLayout, DashboardLayout, or InfoLayout) and
 * logs which one is in use as visit metadata. Logs `null` when the page has no
 * StormUI layout.
 *
 * StormUI layout components tag their root element with
 * `data-sui-component="<kebab-case-name>"`, which this hook inspects.
 *
 * The probe runs once for the document-load visit (see `probeDocumentLoad`)
 * and again for every client-side visit Takt records afterwards (see
 * `probeRouteChange`). Takt mints a new visit on each `pushState`,
 * `replaceState` and `popstate`, and each visit starts with fresh metadata, so
 * a probe that ran only at document load left every client-side visit in the
 * visits table with no `stormLayout` key at all — a majority of the rows for
 * the unified campaign pages, which navigate between tabs client-side.
 *
 * Exactly one metadata write happens per visit. A visit that arrives while an
 * earlier probe is still pending cancels that probe: `addVisitMetadata` always
 * writes to the *current* visit, so a late write from the previous probe would
 * annotate the wrong row.
 */
export default (): void => {
  React.useEffect(() => {
    // One probe is live at a time. `arm` retires whatever is pending before
    // starting the next, so every write lands on the visit it was armed for.
    let cancelProbe = noop;

    const arm = (probe: Probe): void => {
      cancelProbe();

      let settled = false;
      let teardown = noop;

      const log: LogLayout = (layout, source) => {
        if (settled) {
          return;
        }
        settled = true;
        teardown();
        logMetadataWithTakt({ stormLayout: layout, stormLayoutSource: source });
      };

      teardown = probe(log);
      if (settled) {
        // The probe resolved synchronously, before its teardown was known.
        teardown();
      }

      cancelProbe = () => {
        settled = true;
        teardown();
      };
    };

    arm(probeDocumentLoad);
    const unsubscribe = onVisit(() => arm(probeRouteChange));

    return () => {
      unsubscribe();
      cancelProbe();
    };
  }, []);
};
